import { cloneDeep } from 'lodash-es';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
  currentOrPastDateSchema,
  currentOrPastDateUI,
  fullNameNoSuffixSchema,
  fullNameNoSuffixUI,
  radioSchema,
  radioUI,
  ssnSchema,
  ssnUI,
  textSchema,
  textUI,
  titleUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import VaMemorableDateField from 'platform/forms-system/src/js/web-component-fields/VaMemorableDateField';
import VaRadioField from 'platform/forms-system/src/js/web-component-fields/VaRadioField';
import VaTextInputField from 'platform/forms-system/src/js/web-component-fields/VaTextInputField';
import withDeferredValidation from '../components/withDeferredValidation';
import {
  apportionmentInformationText,
  relationshipOptions,
  requestedApportionmentPersonFields,
} from '../definitions/constants';
import VeteranSsnBridge, {
  getVeteranSsn,
  getFormData,
} from '../components/VeteranSsnBridge';

const personFields = requestedApportionmentPersonFields;

const requestedPersonFullNameUI = cloneDeep(fullNameNoSuffixUI());
requestedPersonFullNameUI.first['ui:title'] =
  apportionmentInformationText.firstNameTitle;
requestedPersonFullNameUI.first['ui:errorMessages'] = {
  required: apportionmentInformationText.firstNameError,
};
requestedPersonFullNameUI.middle['ui:title'] =
  apportionmentInformationText.middleInitialTitle;
requestedPersonFullNameUI.last['ui:title'] =
  apportionmentInformationText.lastNameTitle;
requestedPersonFullNameUI.last['ui:errorMessages'] = {
  required: apportionmentInformationText.lastNameError,
};

const requestedPersonFullNameSchema = cloneDeep(fullNameNoSuffixSchema);
if (requestedPersonFullNameSchema?.properties?.middle) {
  requestedPersonFullNameSchema.properties.middle.maxLength = 1;
}

const baseSsnUI = ssnUI();

const childRelationshipValues = [
  'child18To23InSchool',
  'custodianForChildUnder18',
  'childOver18Incapable',
  'other',
  relationshipOptions.child18To23InSchool,
  relationshipOptions.custodianForChildUnder18,
  relationshipOptions.childOver18Incapable,
  relationshipOptions.other,
];

const isYesValue = value => value === true || value === 'Y' || value === 'yes';

const isNoValue = value => value === false || value === 'N' || value === 'no';

const hasAnswer = value => isYesValue(value) || isNoValue(value);

const isChildRelationshipValue = relationship =>
  childRelationshipValues.includes(relationship);

const buildPersonName = person => {
  const name = person?.[personFields.fullName];

  if (!name) {
    return 'Requested person';
  }

  return (
    `${name.first || ''} ${name.middle || ''} ${name.last || ''}`
      .trim()
      .replace(/\s+/g, ' ') || 'Requested person'
  );
};

const buildTitlePersonName = person => {
  const name = person?.[personFields.fullName];

  if (!name) {
    return 'this person';
  }

  return (
    `${name.first || ''} ${name.middle || ''} ${name.last || ''}`
      .trim()
      .replace(/\s+/g, ' ') || 'this person'
  );
};

const buildPossessiveName = person => {
  const name = buildTitlePersonName(person);
  return name === 'this person' ? 'this person’s' : `${name}’s`;
};

const getRequestedPersonAtIndex = (formData, index) =>
  formData?.[personFields.parentObject]?.[index] || formData;

const getPagePerson = (...args) => {
  const [firstArg, secondArg] = args;

  const formData = firstArg?.formData || firstArg;
  const index = firstArg?.index ?? secondArg;

  return getRequestedPersonAtIndex(formData, index);
};

const getRelationshipValue = (formData, index) =>
  getRequestedPersonAtIndex(formData, index)?.[
    personFields.veteranRelationshipDescription
  ];

const isChildRelationship = (formData, index) =>
  isChildRelationshipValue(getRelationshipValue(formData, index));

const relationshipPageTitle = (...args) =>
  `What is ${buildPossessiveName(
    getPagePerson(...args),
  )} relationship to the Veteran?`;

const receiptPageTitle = (...args) =>
  `Does ${buildTitlePersonName(
    getPagePerson(...args),
  )} already receive part of the Veteran’s benefit (apportionment)?`;

const stepchildPageTitle = (...args) =>
  `Is ${buildTitlePersonName(getPagePerson(...args))} the Veteran’s stepchild?`;

const adoptionPageTitle = (...args) =>
  `Has ${buildTitlePersonName(
    getPagePerson(...args),
  )} been legally adopted by another person?`;

const getStepchildLivesWithVeteranValue = (formData, index) =>
  getRequestedPersonAtIndex(formData, index)?.[
    personFields.stepchildLivesWithVeteran
  ];

const getIsStepChildValue = (formData, index) =>
  isYesValue(
    getRequestedPersonAtIndex(formData, index)?.[personFields.isStepChild],
  );

const isStepchildYes = (formData, index) =>
  isYesValue(
    getRequestedPersonAtIndex(formData, index)?.[personFields.isStepChild],
  );

const isStepchildLivingNo = (formData, index) =>
  isNoValue(getStepchildLivesWithVeteranValue(formData, index));

const isOtherRelationship = (formData, index) => {
  const relationship = getRelationshipValue(formData, index);

  return relationship === 'other' || relationship === relationshipOptions.other;
};

const validateOtherRelationship = (
  errors,
  value,
  formData,
  _schema,
  _uiSchema,
  index,
) => {
  if (
    isOtherRelationship(formData, index) &&
    String(value || '').trim() === ''
  ) {
    errors.addError(apportionmentInformationText.otherRelationshipError);
  }
};

const normalizeSsn = ssn => String(ssn ?? '').replace(/[^0-9]/g, '');

const isSameSsn = (a, b) => {
  const left = normalizeSsn(a);
  const right = normalizeSsn(b);

  return left !== '' && right !== '' && left === right;
};

const countClaimantsWithSsn = ssn => {
  const target = normalizeSsn(ssn);

  if (target === '') {
    return 0;
  }

  const people = getFormData()?.[personFields.parentObject] || [];

  return people.filter(
    person => normalizeSsn(person?.[personFields.ssn]) === target,
  ).length;
};

const validateSsnUnique = (errors, value) => {
  if (normalizeSsn(value) === '') {
    return;
  }

  if (isSameSsn(value, getVeteranSsn())) {
    errors.addError(apportionmentInformationText.ssnMatchesVeteranError);
    return;
  }

  if (countClaimantsWithSsn(value) > 1) {
    errors.addError(
      apportionmentInformationText.ssnMatchesAnotherClaimantError,
    );
  }
};

/** @type {ArrayBuilderOptions} */
const options = {
  arrayPath: personFields.parentObject,
  nounSingular: apportionmentInformationText.requestedPeopleNounSingular,
  nounPlural: apportionmentInformationText.requestedPeopleNounPlural,
  required: true,
  maxItems: 4,
  isItemIncomplete: item => {
    const fullName = item?.[personFields.fullName] || {};
    const relationship = item?.[personFields.veteranRelationshipDescription];
    const isOther =
      relationship === 'other' || relationship === relationshipOptions.other;
    const needsChildQuestions = isChildRelationshipValue(relationship);
    const isStepChildValue = item?.[personFields.isStepChild];
    const stepchildLivesWithVeteran =
      item?.[personFields.stepchildLivesWithVeteran];

    return (
      !fullName.first ||
      !fullName.last ||
      !item?.[personFields.ssn] ||
      !relationship ||
      (isOther &&
        !String(
          item?.[personFields.otherRelationshipDescription] || '',
        ).trim()) ||
      !hasAnswer(item?.[personFields.receivesApportionmentNow]) ||
      (needsChildQuestions && !hasAnswer(isStepChildValue)) ||
      (needsChildQuestions &&
        isYesValue(isStepChildValue) &&
        !hasAnswer(stepchildLivesWithVeteran)) ||
      (needsChildQuestions &&
        isYesValue(isStepChildValue) &&
        isNoValue(stepchildLivesWithVeteran) &&
        !item?.[personFields.stepchildDepartureDate]) ||
      (needsChildQuestions &&
        !hasAnswer(item?.[personFields.adoptedChildrenQuestion]))
    );
  },

  text: {
    summaryTitle: props => {
      const count = props.formData?.[personFields.parentObject]?.length || 0;
      return count === 1 ? 'Review claimant' : 'Review claimants';
    },

    getItemName: item => buildPersonName(item),

    summaryAddLinkText: () => 'Add claimant',
    summaryAddButtonText: () => 'Add claimant',
    reviewAddButtonText: () => 'Add claimant',

    alertItemUpdated: ({ itemData, nounSingular }) => {
      const itemName = buildPersonName(itemData);
      return itemName
        ? `Information for ${itemName} has been successfully updated.`
        : `Information for ${nounSingular} has been successfully updated.`;
    },

    cardDescription: item => {
      const parts = [];
      const relationship = item?.[personFields.veteranRelationshipDescription];

      if (relationship) {
        parts.push(relationshipOptions[relationship] || relationship);
      }

      if (hasAnswer(item?.[personFields.receivesApportionmentNow])) {
        parts.push(
          isYesValue(item[personFields.receivesApportionmentNow])
            ? 'Currently receives apportionment'
            : 'Does not currently receive apportionment',
        );
      }

      return parts.join(' • ');
    },
  },
};

const introPage = {
  uiSchema: {
    ...titleUI(
      apportionmentInformationText.requestedPeopleSummaryTitle,
      apportionmentInformationText.requestedPeopleSummaryDescription,
    ),
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

const summaryPage = {
  uiSchema: {
    'view:hasRequestedPeople': arrayBuilderYesNoUI(
      options,
      {
        title: 'Do you have a person to add?',
        hint: 'You can add up to 4 people total.',
        labelHeaderLevel: undefined,
        labelHeaderLevelStyle: undefined,
        labels: {
          Y: 'Yes, add a person',
          N: 'No',
        },
        errorMessages: {
          required:
            'If you have a claimant to add, select "Yes". If not, select "No" to continue.',
        },
      },
      {
        title: 'Do you have another person to add?',
        labelHeaderLevel: 2,
        labels: {
          Y: 'Yes, add another person',
          N: 'No',
        },
        errorMessages: {
          required:
            'If you have another claimant to add, select "Yes". If not, select "No" to continue.',
        },
      },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:hasRequestedPeople': arrayBuilderYesNoSchema,
    },
    required: ['view:hasRequestedPeople'],
  },
};

const personIdentityPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: apportionmentInformationText.requestedPeopleIdentityPageTitle,
      description: apportionmentInformationText.requestedPeopleItemDescription,
      nounSingular: options.nounSingular,
    }),
    [personFields.fullName]: requestedPersonFullNameUI,
    [personFields.ssn]: {
      ...baseSsnUI,
      'ui:errorMessages': {
        required: apportionmentInformationText.ssnRequiredError,
        pattern: apportionmentInformationText.ssnPatternError,
      },
      'ui:description': VeteranSsnBridge,
      'ui:validations': [
        ...(baseSsnUI['ui:validations'] || []),
        validateSsnUnique,
      ],
    },
  },
  schema: {
    type: 'object',
    properties: {
      [personFields.fullName]: requestedPersonFullNameSchema,
      [personFields.ssn]: ssnSchema,
    },
    required: [personFields.fullName, personFields.ssn],
  },
};

const DeferredVaTextField = withDeferredValidation(VaTextInputField);

const personRelationshipPage = {
  uiSchema: {
    ...titleUI(relationshipPageTitle),
    [personFields.veteranRelationshipDescription]: {
      ...radioUI({
        title: apportionmentInformationText.relationshipTitle,
        labels: relationshipOptions,
        errorMessages: {
          required: apportionmentInformationText.relationshipError,
        },
      }),
      'ui:onChange': (value, oldValue, formData, onChange) => {
        if (value === oldValue) {
          return;
        }

        const nextFormData = {
          ...(formData || {}),
          [personFields.veteranRelationshipDescription]: value,
          [personFields.otherRelationshipDescription]:
            value === 'other'
              ? formData?.[personFields.otherRelationshipDescription]
              : undefined,
        };

        if (!isChildRelationshipValue(value)) {
          nextFormData[personFields.isStepChild] = undefined;
          nextFormData[personFields.stepchildLivesWithVeteran] = undefined;
          nextFormData[personFields.stepchildDepartureDate] = undefined;
          nextFormData[personFields.adoptedChildrenQuestion] = undefined;
        }

        onChange(nextFormData);
      },
    },
    [personFields.otherRelationshipDescription]: {
      ...textUI({
        title: apportionmentInformationText.otherRelationshipTitle,
        errorMessages: {
          required: apportionmentInformationText.otherRelationshipError,
        },
      }),

      'ui:required': isOtherRelationship,
      'ui:webComponentField': DeferredVaTextField,
      'ui:validations': [validateOtherRelationship],
      'ui:options': {
        hideIf: (formData, index) => !isOtherRelationship(formData, index),
        hideEmptyValueInReview: true,
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      [personFields.veteranRelationshipDescription]: radioSchema(
        Object.keys(relationshipOptions),
      ),
      [personFields.otherRelationshipDescription]: {
        ...textSchema,
        maxLength: 100,
      },
    },
    required: [personFields.veteranRelationshipDescription],
  },
};

const personReceiptPage = {
  uiSchema: {
    ...titleUI(receiptPageTitle),
    [personFields.receivesApportionmentNow]: radioUI({
      title: ' ',
      hint: apportionmentInformationText.receivesApportionmentNowHint,
      labels: apportionmentInformationText.yesNoLabels,
      errorMessages: {
        required: apportionmentInformationText.receivesApportionmentNowError,
      },
    }),
  },
  schema: {
    type: 'object',
    properties: {
      [personFields.receivesApportionmentNow]: radioSchema(['yes', 'no']),
    },
    required: [personFields.receivesApportionmentNow],
  },
};

const radioTileUI = ({ descriptions, ...uiOptions }) => {
  const ui = radioUI(uiOptions);

  return {
    ...ui,
    'ui:options': {
      ...(ui['ui:options'] || {}),
      descriptions,
      tile: true,
    },
  };
};

const isStepChildRadioUI = radioTileUI({
  title: ' ',
  hint: apportionmentInformationText.isStepchildHint,
  labels: {
    yes: apportionmentInformationText.isStepchildYesLabel,
    no: apportionmentInformationText.isStepchildNoLabel,
  },
  descriptions: {
    yes: apportionmentInformationText.isStepchildYesDescription,
  },
  errorMessages: {
    required: apportionmentInformationText.isStepchildError,
  },
});

const stepchildLivesWithVeteranRadioUI = radioTileUI({
  title: apportionmentInformationText.stepchildLivesWithVeteranQuestionTitle,
  hint: apportionmentInformationText.stepchildLivesWithVeteranHint,
  labels: {
    yes: apportionmentInformationText.stepchildLivesWithVeteranYesLabel,
    no: apportionmentInformationText.stepchildLivesWithVeteranNoLabel,
  },
  descriptions: {
    no: apportionmentInformationText.stepchildLivesWithVeteranNoDescription,
  },
  errorMessages: {
    required: apportionmentInformationText.stepchildLivesWithVeteranError,
  },
});

const stepchildDepartureDateFieldUI = currentOrPastDateUI({
  title: apportionmentInformationText.stepchildDepartureDateTitle,
  hint: apportionmentInformationText.stepchildDepartureDateHint,
  errorMessages: {
    pattern: apportionmentInformationText.stepchildDepartureDateError,
    required: apportionmentInformationText.stepchildDepartureDateError,
  },
});

const DeferredVaRadioField = withDeferredValidation(VaRadioField);
const DeferredVaMemorableDateField =
  withDeferredValidation(VaMemorableDateField);

const personStepchildPage = {
  uiSchema: {
    ...titleUI(stepchildPageTitle),
    [personFields.isStepChild]: {
      ...isStepChildRadioUI,
      'ui:onChange': (value, oldValue, formData, onChange) => {
        if (value === oldValue) {
          return;
        }

        onChange({
          ...(formData || {}),
          [personFields.isStepChild]: value,
          [personFields.stepchildLivesWithVeteran]: undefined,
          [personFields.stepchildDepartureDate]: undefined,
        });
      },
    },
    [personFields.stepchildLivesWithVeteran]: {
      ...stepchildLivesWithVeteranRadioUI,
      'ui:webComponentField': DeferredVaRadioField,
      'ui:required': (formData, index) => isStepchildYes(formData, index),
      'ui:options': {
        ...(stepchildLivesWithVeteranRadioUI['ui:options'] || {}),
        hideIf: (formData, index) => !isStepchildYes(formData, index),
        hideEmptyValueInReview: true,
      },
    },
    [personFields.stepchildDepartureDate]: {
      ...stepchildDepartureDateFieldUI,
      'ui:webComponentField': DeferredVaMemorableDateField,
      'ui:required': (formData, index) =>
        isStepchildYes(formData, index) && isStepchildLivingNo(formData, index),
      'ui:options': {
        ...(stepchildDepartureDateFieldUI['ui:options'] || {}),
        hideIf: (formData, index) =>
          !isStepchildYes(formData, index) ||
          !isStepchildLivingNo(formData, index),
        hideEmptyValueInReview: true,
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      [personFields.isStepChild]: radioSchema(['yes', 'no']),
      [personFields.stepchildLivesWithVeteran]: radioSchema(['yes', 'no']),
      [personFields.stepchildDepartureDate]: currentOrPastDateSchema,
    },
    required: [personFields.isStepChild],
  },
};

const personAdoptionPage = {
  uiSchema: {
    ...titleUI(adoptionPageTitle),
    [personFields.adoptedChildrenQuestion]: radioUI({
      title: apportionmentInformationText.adoptedQuestionTitle,
      hint: apportionmentInformationText.adoptedQuestionHint,
      labels: apportionmentInformationText.yesNoLabels,
      errorMessages: {
        required: apportionmentInformationText.adoptedQuestionError,
      },
    }),
  },
  schema: {
    type: 'object',
    properties: {
      [personFields.adoptedChildrenQuestion]: radioSchema(['yes', 'no']),
    },
    required: [personFields.adoptedChildrenQuestion],
  },
};

export const __testables = {
  buildPersonName,
  buildTitlePersonName,
  buildPossessiveName,
  getRequestedPersonAtIndex,
  getRelationshipValue,
  isChildRelationshipValue,
  isChildRelationship,
  getStepchildLivesWithVeteranValue,
  getIsStepChildValue,
  isYesValue,
  isNoValue,
  hasAnswer,
  isStepchildYes,
  isStepchildLivingNo,
  isOtherRelationship,
  validateOtherRelationship,
  normalizeSsn,
  isSameSsn,
  countClaimantsWithSsn,
  validateSsnUnique,
  relationshipPageTitle,
  receiptPageTitle,
  stepchildPageTitle,
  adoptionPageTitle,
  options,
};

export default arrayBuilderPages(options, pageBuilder => ({
  requestedApportionmentPeopleIntro: pageBuilder.introPage({
    title: apportionmentInformationText.requestedPeopleSummaryTitle,
    path: 'benefit-claimants',
    uiSchema: introPage.uiSchema,
    schema: introPage.schema,
  }),
  requestedApportionmentPeopleSummary: pageBuilder.summaryPage({
    title: apportionmentInformationText.requestedPeopleReviewTitle,
    path: 'review-benefit-claimants',
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  requestedApportionmentPersonDetailsPage: pageBuilder.itemPage({
    title: apportionmentInformationText.requestedPeopleIdentityPageTitle,
    path: 'benefit-claimants/:index/name-and-social-security-number',
    uiSchema: personIdentityPage.uiSchema,
    schema: personIdentityPage.schema,
  }),
  requestedApportionmentPersonRelationshipPage: pageBuilder.itemPage({
    title: apportionmentInformationText.requestedPeopleRelationshipPageTitle,
    path: 'benefit-claimants/:index/relationship-to-veteran',
    uiSchema: personRelationshipPage.uiSchema,
    schema: personRelationshipPage.schema,
  }),
  requestedApportionmentPersonReceiptPage: pageBuilder.itemPage({
    title: apportionmentInformationText.requestedPeopleReceiptPageTitle,
    path: 'benefit-claimants/:index/current-benefit-apportionment',
    uiSchema: personReceiptPage.uiSchema,
    schema: personReceiptPage.schema,
  }),
  requestedApportionmentPersonStepchildPage: pageBuilder.itemPage({
    title: apportionmentInformationText.requestedPeopleStepchildPageTitle,
    path: 'benefit-claimants/:index/veteran-stepchild-status',
    depends: (formData, index) => isChildRelationship(formData, index),
    uiSchema: personStepchildPage.uiSchema,
    schema: personStepchildPage.schema,
  }),
  requestedApportionmentPersonAdoptionPage: pageBuilder.itemPage({
    title: apportionmentInformationText.requestedPeopleAdoptionPageTitle,
    path: 'benefit-claimants/:index/legal-adoption-status',
    depends: (formData, index) => isChildRelationship(formData, index),
    uiSchema: personAdoptionPage.uiSchema,
    schema: personAdoptionPage.schema,
  }),
}));
